import { Injectable } from '@angular/core';
import { DprDetailsSync } from '@shared/models/dpr';
import { numericIdOrZero } from '@shared/utils';

/**
 * Sanitizes the offline batch-sync payload before POST to `synchronize/offlinedata`.
 *
 * Offline-created records carry client-generated temporary ids — GUIDs (e.g.
 * `1c62333e-…`) on entity primary ids and composite strings (e.g. `guid_row_col_status`)
 * on status-tracker cells. The new API binds these into `int`/`int?` fields; Newtonsoft
 * rejects non-numeric strings with a 400 before the action runs.
 *
 * Only section subtrees are walked (activities, personnel, weather, …) up to
 * `sectionDepth` object levels deep. The top-level envelope (`id`, `dprId`, `accountId`,
 * dates) is intentionally left as-is — the backend matches the offline batch by those
 * GUID strings. `accountId` is also excluded inside sections (Azure AD B2C object id,
 * not a DB integer).
 *
 * Coercion rule: non-numeric ids (GUIDs and composite strings) are replaced with `0`;
 * real numeric ids are left unchanged. The server treats `0` as "no database id yet —
 * insert and assign one", so offline-created records get inserted while offline-edited
 * records (real id > 0) continue to update the existing row.
 */
@Injectable({ providedIn: 'root' })
export class OfflineSyncSanitizeService {
  // The DprDetailsSync slots that carry section data (everything except the top-level envelope).
  private static readonly sectionKeys: (keyof DprDetailsSync)[] = [
    'syncDprApprovers',
    'dprOnlineApproval',
    'dprActivities',
    'dprParallelActivities',
    'dprStatusTracker',
    'dprWeather',
    'dprHSE',
    'dprPersonnel',
    'dprConsumable',
    'dprSummary',
    'dprForecastActivities',
    'syncDprAppendixAttachments',
    'dprHidden',
    'dprProgress',
    'dprProgressWithoutTarget',
    'dprTableOfColumns',
    'dprFreeTextFields',
  ];

  sanitizeOfflineChanges(changes: DprDetailsSync[]): DprDetailsSync[] {
    return (changes ?? []).map((change) => this.sanitizeDprDetails(change));
  }

  // Max object-nesting depth from the section value: section → item → nested-sync → nested-item.
  private static readonly sectionDepth = 3;

  private sanitizeDprDetails(change: DprDetailsSync): DprDetailsSync {
    const sanitized = { ...change } as Record<string, unknown>;

    for (const key of OfflineSyncSanitizeService.sectionKeys) {
      const section = sanitized[key];
      if (section != null) {
        sanitized[key] = this.coerceIds(
          section,
          OfflineSyncSanitizeService.sectionDepth
        );
      }
    }

    return sanitized as unknown as DprDetailsSync;
  }

  /**
   * Returns a copy of `value` with every id-like field holding a non-numeric string coerced to `0`.
   * `depth` limits how many object levels are traversed (arrays are containers and don't consume
   * depth). Numbers, numeric strings, nulls, and non-id fields are preserved. Does not mutate the
   * input — offline changes are owned by the NgRx store.
   */
  private coerceIds(value: unknown, depth: number): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.coerceIds(item, depth));
    }

    if (value === null || typeof value !== 'object' || value instanceof Date) {
      return value;
    }

    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as object)) {
      if (this.isIdKey(key) && typeof val === 'string') {
        result[key] = numericIdOrZero(val);
      } else if (depth > 0) {
        result[key] = this.coerceIds(val, depth - 1);
      } else {
        result[key] = val;
      }
    }
    return result;
  }

  // `id` or a foreign-key `<parent>Id`. Excludes list keys (`deletedIds`) and
  // known GUID identity fields that are not numeric entity ids (`accountId` is an
  // Azure AD B2C object id — must not be coerced to 0).
  private static readonly guidStringKeys = new Set(['accountId']);

  private isIdKey(key: string): boolean {
    return (
      !OfflineSyncSanitizeService.guidStringKeys.has(key) &&
      (key === 'id' || (key.endsWith('Id') && !key.endsWith('Ids')))
    );
  }
}
